import { Component } from '@theme/component';

const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * Autoplay (WCAG 2.2.2):
 * - The play/pause toggle stops and restarts rotation.
 * - Rotation waits while the bar is hovered, while keyboard focus is inside it (except on the
 *   toggle, so pressing play starts rotating right away), and while the page is hidden.
 * - Rotation never starts by itself when the visitor prefers reduced motion.
 * - Automatic changes are not announced (aria-live off). Manual changes are (aria-live polite).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [playbackToggle]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Whether keyboard focus is inside the bar, on anything other than the toggle.
   * @type {boolean}
   */
  #focused = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    reducedMotionQuery.addEventListener('change', this.#handleReducedMotionChange);

    if (reducedMotionQuery.matches) {
      this.pause();
    } else {
      this.play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    reducedMotionQuery.removeEventListener('change', this.#handleReducedMotionChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    // Never stack intervals when play is called while already playing
    clearInterval(this.#interval);
    this.#interval = undefined;

    if (!this.#focused && !document.hidden) {
      this.#interval = setInterval(() => {
        if (this.matches(':hover') || this.#focused || document.hidden) return;

        this.next();
      }, interval);
    }

    this.#updateLiveRegion();
  }

  /**
   * Pauses automatic slide playback until play is called again.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  /**
   * Toggles between playing and paused. Used by the play/pause button.
   */
  togglePlayback() {
    if (this.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
    this.#updatePlaybackToggle();
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
    this.#updateLiveRegion();
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || this.#focused || document.hidden) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Swaps the toggle's label to match the current state.
   */
  #updatePlaybackToggle() {
    const { playbackToggle } = this.refs;
    if (!playbackToggle) return;

    const label = this.paused ? playbackToggle.dataset.playLabel : playbackToggle.dataset.pauseLabel;
    if (label) playbackToggle.setAttribute('aria-label', label);
  }

  /**
   * Keeps screen readers quiet while slides change on their own, and lets them announce
   * changes the visitor makes with the arrows.
   */
  #updateLiveRegion() {
    if (!this.autoplay) return;

    this.setAttribute('aria-live', this.#interval === undefined ? 'polite' : 'off');
  }

  /**
   * Waits while keyboard focus is inside the bar. Mouse clicks also focus buttons, but hover
   * already covers those, and waiting on them would keep rotation stopped after the mouse
   * leaves. Focus on the toggle doesn't count.
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    const { target } = event;
    const focused =
      target instanceof Element &&
      target.matches(':focus-visible') &&
      !this.refs.playbackToggle?.contains(target);

    if (focused === this.#focused) return;

    this.#focused = focused;

    if (focused) {
      this.suspend();
    } else {
      this.resume();
    }
  };

  /**
   * Resumes once focus leaves the bar. Moves between controls inside the bar are handled by focusin.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (!this.#focused) return;

    this.#focused = false;
    this.resume();
  };

  /**
   * Waits while the page is hidden. This doesn't count as the visitor pausing, so rotation
   * picks up again when the page is shown.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Stops rotation if the visitor turns on reduced motion while the page is open.
   */
  #handleReducedMotionChange = () => {
    if (reducedMotionQuery.matches) this.pause();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
